import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowRight, Check, Star, MapPin, ChevronDown } from "lucide-react";
import { PHONE_TEL, MAPS_URL } from "@/lib/site";

const HERO_IMG =
  "https://images.pexels.com/photos/261985/pexels-photo-261985.jpeg?auto=compress&cs=tinysrgb&w=1600";

const LINES = ["Buy Your Next Car", "With Complete", "Confidence."];

const BADGES = [
  { label: "Certified Inspection", cls: "right-[6%] top-[22%]", dur: 5 },
  { label: "Same Day Service", cls: "right-[16%] top-[44%]", dur: 6.5 },
  { label: "Detailed PDF Report", cls: "right-[4%] top-[62%]", dur: 5.6 },
  { label: "OBD Diagnostics", cls: "right-[20%] top-[78%]", dur: 7 },
];

export const Hero = ({ onBook }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.22]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden"
    >
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Luxury car inspection in Hyderabad"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="hero-vignette absolute inset-0" />

      {BADGES.map((b, i) => (
        <motion.div
          key={b.label}
          data-testid={`hero-badge-${b.label.toLowerCase().replace(/\s+/g, "-")}`}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: [0, -10, 0] }}
          transition={{
            opacity: { delay: 1 + i * 0.15, duration: 0.8 },
            y: { duration: b.dur, repeat: Infinity, ease: "easeInOut", delay: i * 0.4 },
          }}
          className={`glass-chip absolute z-10 hidden items-center gap-2 rounded-full px-4 py-2.5 lg:flex ${b.cls}`}
        >
          <Check className="h-4 w-4 text-gold" strokeWidth={3} />
          <span className="text-xs font-bold tracking-wide text-white/90">
            {b.label}
          </span>
        </motion.div>
      ))}

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-28 pt-36 md:px-8"
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mb-8 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 backdrop-blur-md"
        >
          <MapPin className="h-3.5 w-3.5 text-gold" />
          <span className="text-xs font-bold uppercase tracking-[0.22em] text-white/80">
            Hyderabad · Open 24 Hours
          </span>
        </motion.div>

        <h1 className="max-w-4xl font-heading text-5xl font-extrabold leading-[0.98] tracking-tighter sm:text-6xl lg:text-8xl">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 2 ? "text-gold" : "text-white"}`}
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{
                  duration: 0.9,
                  delay: 0.25 + i * 0.13,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subheading"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.75 }}
          className="mt-7 max-w-xl text-base leading-relaxed text-white/65 md:text-lg"
        >
          Professional Pre-Owned &amp; New Car Inspection Services in Hyderabad.
          Get a detailed inspection report before you buy and avoid expensive
          surprises.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-book-inspection-btn"
            onClick={onBook}
            className="group flex items-center gap-3 rounded-full bg-gold px-8 py-4 font-heading text-base font-extrabold text-black transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:bg-gold-light hover:shadow-[0_16px_40px_-12px_rgba(212,175,55,0.5)]"
          >
            Book an Inspection
            <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <a
            data-testid="hero-call-now-btn"
            href={PHONE_TEL}
            className="glass-chip flex items-center gap-3 rounded-full px-8 py-4 font-heading text-base font-bold text-white transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-gold/50"
          >
            <Phone className="h-5 w-5 text-gold" />
            Call Now
          </a>
        </motion.div>

        <motion.div
          data-testid="hero-rating-row"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-3"
        >
          <a
            data-testid="hero-google-rating-link"
            href={MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 transition-opacity duration-300 hover:opacity-80"
          >
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-4 w-4 fill-gold text-gold" />
            ))}
            <span className="ml-1 text-sm font-semibold text-white/80 underline-offset-4 hover:underline">
              Google Rated
            </span>
          </a>
          <span className="hidden h-4 w-px bg-white/20 sm:block" />
          <span className="text-sm font-semibold text-white/60">
            100+ Vehicles Inspected
          </span>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
      >
        <ChevronDown className="h-6 w-6 animate-bounce text-white/40" />
      </motion.div>
    </section>
  );
};
